import React, { useState, useEffect } from "react";
import { useAuth } from "./Shared";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { isDiagnosticsMode } from "../lib/utils";
import { ChevronDown, ChevronUp, Bug } from "lucide-react";

/**
 * Pilot Debug Panel - Diagnostics-only panel for preview testing
 * Shows pilot mode status, auth state, and action buttons
 * Only visible when diagnostics mode is enabled (?debug=1 or localStorage prepmate_debug="1")
 */
export function PilotDebugPanel({ joinContext = null }) {
  const { user, isPilot, configLoading, lastAuthError } = useAuth();
  const { toast } = useToast();
  const [collapsed, setCollapsed] = useState(false);
  const [binding, setBinding] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Get last invite token from localStorage
  const lastInviteToken = typeof localStorage !== 'undefined' 
    ? localStorage.getItem('prepmate_last_invite_token') || null 
    : null;

  // Only render if diagnostics mode is enabled
  if (!isDiagnosticsMode()) {
    return null;
  }

  const handleEnableDiagnostics = () => {
    localStorage.setItem('prepmate_debug', '1');
    toast({ title: "Diagnostics enabled", description: "Reloading page..." });
    setTimeout(() => window.location.reload(), 500);
  };

  const handleBindToVendor = async (vendorId = "v-austin-01") => {
    if (!user) {
      toast({ title: "Not logged in", description: "Please sign in first" });
      return;
    }
    
    setBinding(true);
    try {
      const token = localStorage.getItem("prepmate_token");
      const res = await fetch("/api/users/me/vendor", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ vendor_id: vendorId })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast({ title: "Success", description: `Bound to vendor: ${data.vendor_id}` });
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast({ title: "Error", description: data.detail || "Failed to bind" });
      }
    } catch (err) {
      toast({ title: "Error", description: "Network error" });
    } finally {
      setBinding(false);
    }
  };

  const handleClearBinding = async () => {
    if (!user) {
      toast({ title: "Not logged in", description: "Please sign in first" });
      return;
    }
    
    setClearing(true);
    try {
      const token = localStorage.getItem("prepmate_token");
      const res = await fetch("/api/users/me/vendor", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ vendor_id: null })
      });
      
      const data = await res.json();
      if (res.ok) {
        toast({ title: "Success", description: "Vendor binding cleared" });
        setTimeout(() => window.location.reload(), 1000);
      } else {
        toast({ title: "Error", description: data.detail || "Failed to clear" });
      }
    } catch (err) {
      toast({ title: "Error", description: "Network error" });
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-slate-900 text-white rounded-lg shadow-xl border border-slate-700 text-xs">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 bg-slate-800 rounded-t-lg cursor-pointer"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <Bug size={14} className="text-amber-400" />
          <span className="font-semibold">Pilot Debug Panel</span>
        </div>
        {collapsed ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>

      {!collapsed && (
        <div className="p-3 space-y-3">
          {/* Pilot Mode Status */}
          <div>
            <div className="text-slate-400 mb-1">Pilot Mode</div>
            <div className="flex items-center gap-2">
              {configLoading ? (
                <span className="text-slate-500">Loading...</span>
              ) : (
                <>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${isPilot ? 'bg-green-600' : 'bg-slate-600'}`}>
                    {isPilot ? 'ON' : 'OFF'}
                  </span>
                  <span className="text-slate-400">from /api/config</span>
                </>
              )}
            </div>
          </div>

          {/* Auth State */}
          <div>
            <div className="text-slate-400 mb-1">Auth State</div>
            {user ? (
              <div className="bg-slate-800 rounded p-2 space-y-1">
                <div><span className="text-slate-500">email:</span> {user.email}</div>
                <div><span className="text-slate-500">role:</span> <span className={user.role === 'company' ? 'text-blue-400' : 'text-green-400'}>{user.role}</span></div>
                <div>
                  <span className="text-slate-500">vendor_id:</span>{' '}
                  {user.vendor_id ? (
                    <span className="text-amber-400">{user.vendor_id}</span>
                  ) : (
                    <span className="text-red-400">null (not bound)</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-red-400">Not logged in</div>
            )}
          </div>

          {/* Last Auth Error */}
          {lastAuthError && (
            <div>
              <div className="text-slate-400 mb-1">Last Auth Error</div>
              <div className="bg-red-900/50 text-red-300 rounded p-2 text-[10px]">
                {lastAuthError}
              </div>
            </div>
          )}

          {/* Last Invite Token */}
          {lastInviteToken && (
            <div>
              <div className="text-slate-400 mb-1">Last Invite Token</div>
              <div className="bg-slate-800 rounded p-2 text-[10px] break-all">
                {lastInviteToken}
              </div>
            </div>
          )}

          {/* Join Context (if on /join/:token) */}
          {joinContext && (
            <div>
              <div className="text-slate-400 mb-1">Join Context</div>
              <div className="bg-slate-800 rounded p-2 space-y-1">
                <div><span className="text-slate-500">token:</span> {joinContext.token || 'none'}</div>
                {joinContext.vendor ? (
                  <>
                    <div><span className="text-slate-500">vendor_id:</span> {joinContext.vendor.id}</div>
                    <div><span className="text-slate-500">vendor_name:</span> {joinContext.vendor.name}</div>
                  </>
                ) : joinContext.error ? (
                  <div className="text-red-400">Error: {joinContext.error}</div>
                ) : (
                  <div className="text-slate-500">Loading vendor...</div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-2 pt-2 border-t border-slate-700">
            <div className="text-slate-400 mb-1">Actions</div>
            
            <Button
              size="sm"
              variant="outline"
              className="w-full h-7 text-xs bg-slate-800 border-slate-600 hover:bg-slate-700"
              onClick={handleEnableDiagnostics}
            >
              Enable Diagnostics (reload)
            </Button>

            <Button
              size="sm"
              variant="outline"
              className="w-full h-7 text-xs bg-amber-900 border-amber-700 hover:bg-amber-800 text-amber-100"
              onClick={() => handleBindToVendor("v-austin-01")}
              disabled={binding || !user}
            >
              {binding ? "Binding..." : "Bind to v-austin-01"}
            </Button>

            {user?.vendor_id && (
              <Button
                size="sm"
                variant="outline"
                className="w-full h-7 text-xs bg-red-900 border-red-700 hover:bg-red-800 text-red-100"
                onClick={handleClearBinding}
                disabled={clearing}
              >
                {clearing ? "Clearing..." : "Clear Vendor Binding"}
              </Button>
            )}
          </div>

          {/* Diagnostics Mode Indicator */}
          <div className="text-center text-slate-500 text-[10px] pt-1 border-t border-slate-700">
            Visible because ?debug=1 or localStorage.prepmate_debug="1"
          </div>
        </div>
      )}
    </div>
  );
}

export default PilotDebugPanel;
